import { LUX_LOYALTY_TIERS_CONFIG } from '@luxuryescapes/lib-lux-loyalty';

import { Payment } from '~/types/services/payment';

import { EmptyArray } from './arrayUtils';
import featureToggle from './featureToggle';

export function isLuxLoyaltyEnabled(): boolean {
  return featureToggle.availableToShow('SHOW_LUX_LOYALTY');
}

/**
 * Returns a friendly display name for any LuxLoyaltyTier. Falls back to a
 * capitalised slug when the published lib doesn't yet have a config entry for
 * the tier (e.g. the new `infinite` tier prior to the lib bump).
 */
export function getLuxLoyaltyTierDisplayName(tier: App.LuxLoyaltyTier): string {
  const config = (LUX_LOYALTY_TIERS_CONFIG as Partial<Record<App.LuxLoyaltyTier, { displayName: string }>>)[tier];
  return config?.displayName ?? tier.charAt(0).toUpperCase() + tier.slice(1);
}

export interface ItemPointsPaymentDetails {
  itemId: string;
  currency: string;
  pointsPaid: number;
  pointsPaidCurrencyValue: number;
  priceInCurrency: number;
  priceInPoints: number;
}

/**
 * Extracts the points payments for an item
 */
export function filterPointsPaymentsForItem(
  /** Id of the item for which to find points payments */
  itemId: string,
  /** The array of payments associated with an order */
  payments: Array<Payment>,
): Array<ItemPointsPaymentDetails> {
  const payment = payments.find((payment) => payment.type === 'le_points' && payment.item_ids.includes(itemId));
  if (!payment) {
    return EmptyArray;
  }
  const itemsBreakdown = payment.items_breakdown?.find((breakdown) => breakdown?.id_items === itemId);
  if (!itemsBreakdown) {
    return EmptyArray;
  }
  const pointsDetails = itemsBreakdown?.item_le_points_details;
  if (!pointsDetails) {
    return EmptyArray;
  }

  return [
    {
      itemId: itemsBreakdown.item_le_points_details?.item_id,
      currency: payment.currency,
      pointsPaid: itemsBreakdown.item_le_points_details?.points_paid,
      pointsPaidCurrencyValue: itemsBreakdown.item_le_points_details?.points_paid_currency_value,
      priceInCurrency: itemsBreakdown.item_le_points_details?.price_in_currency,
      priceInPoints: itemsBreakdown.item_le_points_details?.price_in_points,
    },
  ];
}

/**
 * Extracts the pending points for an item from all pending points associated with an order
 * @returns Pending points for the item or undefined
 */
export function filterPendingPointsForItem(
  /** Id of the item for which to find pending points */
  itemId: string,
  /** Pending points returned from request to lux loyalty */
  pendingPoints: Array<App.LuxLoyaltyPendingPoint>,
): Array<App.LuxLoyaltyPendingPoint> {
  return pendingPoints.filter((pending) => pending.itemId === itemId);
}
export const luxLoyaltyBenefitDescriptionMap: Map<App.LuxLoyaltyBenefitType, string> = new Map([
  ['airport_transfer', 'Airport Transfers'],
  ['discounted_insurance', 'Discounted Travel & Booking Protection'],
  ['early_access', 'Early Access To Deals'],
  ['hidden_access', 'Hidden Access To Deals'],
  ['lounge_pass', 'Lounge Pass Access'],
  ['phone_support', 'Priority 24/7 Phone Support'],
  ['tour_exp_credit', 'Tour Optional Extras'],
  ['travelshoot_credit', 'Travelshoot Vouchers'],
  ['hotel_upgrade', 'Complimentary Hotel Upgrades'],
  ['esim_package', 'eSIM Data Pack'],
  ['family_and_friends_access', 'Family & Friends Access'],
]);

export function filterAccrualsForItem(
  itemId: string,
  accruals: Array<App.LuxLoyaltyAccrualItem>,
): Array<App.LuxLoyaltyAccrualItem> {
  return accruals.filter((acc) => acc.itemId === itemId);
}
